import React, { type RefObject } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Archive,
  ArchiveRestore,
  CheckCheck,
  ExternalLink,
  Loader2,
  Search as SearchIcon,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { type FeedFilter, type FeedItem, type FeedSubscription, prefetchExtraction } from "@/lib/feedReader";
import { FeedItemRowCard } from "./FeedItemRowCard";
import { FILTER_LABELS, FEED_TONE, formatDate } from "./feedReaderHelpers";

interface Props {
  filter: FeedFilter;
  setFilter: (f: FeedFilter) => void;
  searchInput: string;
  setSearchInput: (v: string) => void;
  searchInputRef: RefObject<HTMLInputElement>;
  searchQuery: string;
  loading: boolean;
  visibleItems: FeedItem[];
  subscriptions: FeedSubscription[];
  subById: Map<string, FeedSubscription>;
  selectedItemId: string | null;
  itemRowRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  showChips: boolean;
  wsLabel: (id: string) => string;
  selectedSub: string | null;
  unreadShownCount: number;
  markingAllRead: boolean;
  selectItem: (id: string) => void;
  onMarkAllRead: (subscriptionId?: string) => void;
  onStar: (item: FeedItem) => void;
  onToggleRead: (item: FeedItem) => void;
  onArchive: (item: FeedItem) => void;
  onDeleteItem: (item: FeedItem) => void;
  onOpenExternal: (item: FeedItem) => void;
}

export const FeedItemsListPane: React.FC<Props> = ({
  filter,
  setFilter,
  searchInput,
  setSearchInput,
  searchInputRef,
  searchQuery,
  loading,
  visibleItems,
  subscriptions,
  subById,
  selectedItemId,
  itemRowRefs,
  showChips,
  wsLabel,
  selectedSub,
  unreadShownCount,
  markingAllRead,
  selectItem,
  onMarkAllRead,
  onStar,
  onToggleRead,
  onArchive,
  onDeleteItem,
  onOpenExternal,
}) => {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border/60 px-3 py-2 flex flex-wrap items-center gap-2">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as FeedFilter)}>
          <TabsList>
            {(Object.keys(FILTER_LABELS) as FeedFilter[]).map((f) => (
              <TabsTrigger key={f} value={f}>{FILTER_LABELS[f]}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative ml-auto">
          <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            ref={searchInputRef}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search…  (press /)"
            className="h-9 w-56 pl-7 pr-7"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
              aria-label="Clear search"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </div>
        {filter === "unread" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onMarkAllRead(selectedSub ?? undefined)}
            disabled={markingAllRead || unreadShownCount === 0}
            title="Mark all visible as read"
          >
            {markingAllRead ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-1" />}
            Mark all read
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}

          {!loading && visibleItems.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                {subscriptions.length === 0
                  ? "Add a feed to start receiving new items here."
                  : searchQuery
                  ? `No items match "${searchQuery}".`
                  : filter === "unread"
                  ? "Inbox zero. Nice."
                  : `No ${FILTER_LABELS[filter].toLowerCase()} items.`}
              </CardContent>
            </Card>
          )}

          {!loading && visibleItems.map((item) => {
            const sub = subById.get(item.subscription_id);
            const isStarred = !!item.starred_at;
            const isArchived = !!item.archived_at;
            const isSelected = selectedItemId === item.id;
            return (
              <FeedItemRowCard
                key={item.id}
                ref={(el) => {
                  if (el) itemRowRefs.current.set(item.id, el);
                  else itemRowRefs.current.delete(item.id);
                }}
                onClick={() => selectItem(item.id)}
                onMouseEnter={() => prefetchExtraction(item)}
                onFocus={() => prefetchExtraction(item)}
                isRead={!!item.read_at}
                isSelected={isSelected}
              >
                <CardContent className="p-3 space-y-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <span className="truncate">{sub?.title ?? "Feed"}</span>
                      {item.author && <><span>·</span><span className="truncate">{item.author}</span></>}
                      {item.published_at && <><span>·</span><span>{formatDate(item.published_at)}</span></>}
                      {showChips && (
                        <Badge variant="outline" className="text-xs">{wsLabel(item.workspace_id)}</Badge>
                      )}
                    </div>
                    <div
                      className="font-semibold leading-snug"
                      style={{ color: FEED_TONE.title }}
                    >
                      {item.title || "(Untitled)"}
                      {item.link && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onOpenExternal(item); }}
                          className="ml-1 inline-flex align-middle hover:opacity-80"
                          style={{ color: FEED_TONE.title }}
                          title="Open in new tab (o)"
                          aria-label="Open in new tab"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {item.summary && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.summary}</p>
                    )}
                  </div>
                  <div
                    className="flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: FEED_TONE.title }}
                  >
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onStar(item)}
                      title={isStarred ? "Unstar (s)" : "Star (s)"}
                      style={{ color: FEED_TONE.title }}
                    >
                      <Star
                        className="h-4 w-4"
                        style={isStarred ? { fill: FEED_TONE.title, color: FEED_TONE.title } : undefined}
                      />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onToggleRead(item)}
                      title={item.read_at ? "Mark unread (r)" : "Mark read (r)"}
                      style={{ color: FEED_TONE.title }}
                    >
                      <CheckCheck className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onArchive(item)}
                      title={isArchived ? "Move to inbox (e)" : "Archive (e)"}
                      style={{ color: FEED_TONE.title }}
                    >
                      {isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDeleteItem(item)}
                      title="Delete from feed"
                      aria-label="Delete from feed"
                      className="hover:text-destructive"
                      style={{ color: FEED_TONE.title }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </FeedItemRowCard>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
